import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Grid,
  CircularProgress,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Pagination
} from '@mui/material';
import {
  Store as StoreIcon,
  ShoppingCart as ShoppingCartIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import axios from '../../../api/axios';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import SidebarWithAccordion from '../../../components/Dashboard/SidebarWithAccordion';
import { createTeacherSidebarConfig } from '../../../components/Dashboard/TeacherSidebarConfig';
import UserAvatar from '../../../components/Avatar/UserAvatar';
import NotebookAgentAvatar from '../../../components/Avatar/NotebookAgentAvatar';

const ITEMS_PER_PAGE = 6;

const getNotebookImage = (title) =>
  `https://picsum.photos/seed/${encodeURIComponent(title)}/400/200`;

const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: 'none',
  boxShadow: 'none',
  transition: 'all 0.3s',
  cursor: 'pointer',
  '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 },
};

function MarketplacePage() {
  const navigate = useNavigate();
  const { user, selectedCourse, logout } = useContext(AuthContext);
  const [notebooks, setNotebooks] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [targetCourseCode, setTargetCourseCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [fetchError, setFetchError] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  useEffect(() => {
    setPage(1);
  }, [selectedCourse?.code]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const [notebooksRes, enrollmentsRes] = await Promise.all([
          axios.get('/notes/marketplace'),
          axios.get('/enrollments')
        ]);
        setNotebooks(notebooksRes.data || []);
        const courses = (enrollmentsRes.data || []).filter(
          (e) => e.code && e.role === 'teacher'
        );
        setMyCourses(courses);
        if (courses.length > 0 && !targetCourseCode) {
          setTargetCourseCode(courses[0].code);
        }
      } catch (error) {
        console.error('Error fetching marketplace:', error);
        setNotebooks([]);
        const status = error.response?.status;
        const msg = error.response?.data?.error || error.message;
        if (status === 404) {
          setFetchError('商城接口未找到 (404)。请确保后端已部署最新版本，并正确配置 REACT_APP_API_URL。');
        } else if (status === 401 || status === 403) {
          setFetchError('请先登录教师账号后再访问商城。');
        } else {
          setFetchError(msg || '加载失败，请检查网络或稍后重试。');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only; targetCourseCode is for add dialog
  }, []);

  const handleAddToCart = (notebook, e) => {
    e?.stopPropagation();
    setCart((prev) => {
      if (prev.some((n) => n.id === notebook.id)) return prev;
      return [...prev, notebook];
    });
    setSnackbarOpen(true);
  };

  const handleRemoveFromCart = (notebookId) => {
    setCart((prev) => prev.filter((n) => n.id !== notebookId));
  };

  const handleOpenAddToCourseDialog = () => {
    setCartDrawerOpen(false);
    setTargetCourseCode(myCourses[0]?.code || selectedCourse?.code || '');
    setAddError('');
    setAddDialogOpen(true);
  };

  const handleAddToCourse = async () => {
    if (cart.length === 0 || !targetCourseCode) return;
    setAdding(true);
    setAddError('');
    try {
      for (const notebook of cart) {
        await axios.post('/notes/marketplace/add-to-course', {
          sourceNotebookId: notebook.id,
          targetCourseCode: targetCourseCode.trim().toUpperCase()
        });
      }
      setAddDialogOpen(false);
      setCart([]);
      navigate(`/notebooks/${targetCourseCode}`);
    } catch (error) {
      setAddError(
        error.response?.data?.error || '添加失败，请重试'
      );
    } finally {
      setAdding(false);
    }
  };

  const sidebarConfig = createTeacherSidebarConfig({
    navigate,
    user,
    selectedCourse,
    onLogout: handleLogout,
    currentPath: '/marketplace'
  });
  const drawerContent = (
    <SidebarWithAccordion
      {...sidebarConfig}
      onSettingsClick={() => navigate('/teacher/settings')}
    />
  );

  return (
    <DashboardLayout
      drawerContent={drawerContent}
      appBarTitle="笔记本商城"
      courseName={selectedCourse?.name}
      courseTermCode={selectedCourse?.termCode}
      mobileOpen={mobileOpen}
      onDrawerToggle={handleDrawerToggle}
      onLogout={handleLogout}
    >
      <Container maxWidth="lg" sx={{ pt: 2, pb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ flex: 1 }}>
            笔记本商城
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <IconButton
              onClick={() => setCartDrawerOpen(true)}
              sx={{ color: 'inherit', p: 0 }}
              aria-label="购物车"
            >
              <Badge badgeContent={cart.length} color="primary">
                <ShoppingCartIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              </Badge>
            </IconButton>
            <Typography variant="caption" color="text.secondary">购物车</Typography>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          浏览其他老师创建的笔记本，可添加到{selectedCourse?.name ? `「${selectedCourse.name}」` : '您教授的课程'}中
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (() => {
          const currentCourseCode = selectedCourse?.code?.toUpperCase?.();
          const filteredNotebooks = currentCourseCode
            ? notebooks.filter((n) => (n.courseCode || '').toUpperCase() !== currentCourseCode)
            : notebooks;
          const totalPages = Math.max(1, Math.ceil(filteredNotebooks.length / ITEMS_PER_PAGE));
          const effectivePage = Math.min(page, totalPages);
          const paginatedNotebooks = filteredNotebooks.slice(
            (effectivePage - 1) * ITEMS_PER_PAGE,
            effectivePage * ITEMS_PER_PAGE
          );
          return filteredNotebooks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <StoreIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {fetchError ? '加载失败' : '暂无可用笔记本'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fetchError || (currentCourseCode
                  ? `当前课程「${selectedCourse?.name}」已有的笔记本不显示，其他课程的笔记本将在此展示`
                  : '商城中的笔记本来自其他老师创建的课程，当前暂无其他课程的笔记本')}
              </Typography>
            </Box>
          ) : (
          <Box>
          <Grid container spacing={3}>
            {paginatedNotebooks.map((notebook) => (
              <Grid item xs={12} sm={6} md={4} key={notebook.id}>
                <Card sx={cardSx} onClick={() => navigate(`/notebook/${notebook.id}`)}>
                  <CardMedia
                    sx={{ height: 140 }}
                    image={getNotebookImage(notebook.title)}
                    title={notebook.title}
                  />
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      {notebook.creator ? (
                        <UserAvatar
                          user={{ name: notebook.creator, authorAvatar: notebook.creatorAvatar }}
                          avatar={notebook.creatorAvatar}
                          size={48}
                          showFrame={false}
                        />
                      ) : (
                        <NotebookAgentAvatar
                          agentAvatar={notebook.agentAvatar}
                          notebookId={notebook.id}
                          size={48}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight="600" noWrap>
                          {notebook.title}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={notebook.courseCode}
                            size="small"
                            variant="outlined"
                          />
                          {notebook.creator && (
                            <Typography variant="caption" color="text.secondary">
                              创建者：{notebook.creator}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {notebook.description || '暂无描述'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleAddToCart(notebook, e)}
                    >
                      添加到购物车
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/notebook/${notebook.id}`);
                      }}
                    >
                      预览
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={effectivePage}
                onChange={(_, p) => setPage(p)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
          </Box>
          );
        })()}
      </Container>

      {/* 购物车抽屉 */}
      <Drawer
        anchor="right"
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            购物车 ({cart.length})
          </Typography>
          {cart.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              购物车为空
            </Typography>
          ) : (
            <>
              <List dense>
                {cart.map((n) => (
                  <ListItem
                    key={n.id}
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => handleRemoveFromCart(n.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={n.title} secondary={n.courseCode} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleOpenAddToCourseDialog}
              >
                添加到课程
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>添加到课程</DialogTitle>
        <DialogContent>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            将购物车中的 {cart.length} 个笔记本添加到：
          </Typography>
          {myCourses.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              您还没有教授的课程，请先在课程页面创建或添加课程
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>选择课程</InputLabel>
              <Select
                value={targetCourseCode}
                label="选择课程"
                onChange={(e) => setTargetCourseCode(e.target.value)}
              >
                {myCourses.map((c) => (
                  <MenuItem key={c.id || c.code} value={c.code}>
                    {c.name} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleAddToCourse}
            variant="contained"
            disabled={!targetCourseCode || adding || myCourses.length === 0 || cart.length === 0}
          >
            {adding ? '添加中...' : '确认添加'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="已添加到购物车"
        sx={{ zIndex: 9999 }}
      />
    </DashboardLayout>
  );
}

export default MarketplacePage;
